import { useState } from 'react';
import { motion } from 'framer-motion';
import { verifyCertificateBySerial } from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Search, Shield, CheckCircle, XCircle, Gem, Weight, FileCheck, QrCode, AlertCircle } from 'lucide-react';

export default function VerifyPage() {
  const [serial, setSerial] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!serial.trim()) return;
    
    setLoading(true);
    setSearched(true);
    try {
      const res = await verifyCertificateBySerial(serial.trim());
      setResult(res.data);
    } catch (err) {
      setResult({ valid: false, message: 'Error verifying certificate' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="verify-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight uppercase">Verify Authenticity</h1>
          </div>
          <p className="text-muted-foreground mb-8">Enter the serial number from your collectible to verify its authenticity</p>
        </motion.div>

        {/* Search Form */}
        <motion.form 
          onSubmit={handleVerify}
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex gap-3">
            <Input
              data-testid="serial-input"
              type="text"
              placeholder="Enter serial number (e.g., ZWT-GOLD-0001)"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              className="flex-1 h-12 bg-secondary/50 border-border text-base"
            />
            <Button 
              type="submit" 
              disabled={loading || !serial.trim()}
              className="h-12 px-6 bg-primary text-primary-foreground"
              data-testid="verify-button"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Verify</>
              )}
            </Button>
          </div>
        </motion.form>

        {/* Result */}
        {searched && result && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }}
            className={`border rounded-sm overflow-hidden ${
              result.valid 
                ? 'bg-green-500/5 border-green-500/20' 
                : 'bg-red-500/5 border-red-500/20'
            }`}
          >
            {/* Status Header */}
            <div className={`p-6 text-center ${result.valid ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
              {result.valid ? (
                <>
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <h2 className="font-display text-xl font-bold uppercase text-green-400">Authentic Product</h2>
                  <p className="text-sm text-green-400/70 mt-1">This collectible is verified and genuine</p>
                </>
              ) : (
                <>
                  <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <h2 className="font-display text-xl font-bold uppercase text-red-400">Not Found</h2>
                  <p className="text-sm text-red-400/70 mt-1">{result.message || 'No certificate found for this serial number'}</p>
                </>
              )}
            </div>

            {/* Certificate Details */}
            {result.valid && result.certificate && (
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 font-bold text-xs uppercase">
                    <CheckCircle className="w-3 h-3 mr-1" /> {result.authenticity_status}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">{result.certificate.certificate_id}</span>
                </div>

                {result.certificate.image_url && (
                  <img 
                    src={result.certificate.image_url} 
                    alt={result.certificate.collectible_name} 
                    className="w-full aspect-video object-cover rounded-sm"
                    data-testid="cert-image"
                  />
                )}

                <h3 className="font-display text-xl font-bold" data-testid="cert-name">{result.certificate.collectible_name}</h3>

                <Separator className="bg-white/10" />

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-secondary/50 border border-border/40 p-3 rounded-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Gem className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Metal Type</span>
                    </div>
                    <p className="font-display font-bold capitalize">{result.certificate.metal_type}</p>
                  </div>
                  <div className="bg-secondary/50 border border-border/40 p-3 rounded-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Weight className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Weight</span>
                    </div>
                    <p className="font-display font-bold">{result.certificate.weight}g</p>
                  </div>
                  <div className="bg-secondary/50 border border-border/40 p-3 rounded-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Purity</span>
                    </div>
                    <p className="font-display font-bold">{result.certificate.purity}</p>
                  </div>
                  <div className="bg-secondary/50 border border-border/40 p-3 rounded-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <FileCheck className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">BIS Hallmark</span>
                    </div>
                    <p className="font-display font-bold text-sm">{result.certificate.bis_hallmark || 'N/A'}</p>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Serial Number</span>
                    <span className="font-mono text-primary">{result.certificate.serial_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Fineness</span>
                    <span className="font-mono">{result.certificate.purity_fineness}</span>
                  </div>
                  {result.certificate.drop_name && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Drop</span>
                      <span>{result.certificate.drop_name}</span>
                    </div>
                  )}
                  {result.certificate.mint_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mint Date</span>
                      <span className="font-mono">{new Date(result.certificate.mint_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className={`p-4 text-center border-t ${result.valid ? 'border-green-500/20 bg-green-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
              {result.valid ? (
                <p className="text-xs text-muted-foreground">This certificate is digitally verified by SŌHM</p>
              ) : (
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <AlertCircle className="w-4 h-4" />
                  <p>If you believe this is an error, please contact support</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Help Section */}
        {!searched && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.2 }}
            className="bg-card border border-border/40 rounded-sm p-6"
          >
            <h3 className="font-display font-bold mb-3 flex items-center gap-2">
              <QrCode className="w-4 h-4 text-primary" /> Where to find the serial number?
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Check the engraved serial on your collectible</li>
              <li>• Scan the QR code on the product packaging</li>
              <li>• Find it in your order confirmation email</li>
              <li>• View it in your Vault under "My Collection"</li>
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
}
